import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AdminUserListParams, AdminUserRole, AdminUserStatusFilter } from '@/types/adminUser';

export interface UserQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  status: AdminUserStatusFilter;
  role: AdminUserRole | 'all';
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  status: AdminUserStatusFilter | null;
  role: AdminUserRole | 'all' | null;
}>;

const DEFAULTS: UserQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  search: '',
  status: 'all',
  role: 'all',
};

function parseStatus(value: string | null): AdminUserStatusFilter {
  if (value === 'active' || value === 'inactive') return value;
  return 'all';
}

function parseRole(value: string | null): AdminUserRole | 'all' {
  if (value === 'admin' || value === 'manager') return value;
  return 'all';
}

export function useUserQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<UserQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc',
      search: searchParams.get('search') || '',
      status: parseStatus(searchParams.get('status')),
      role: parseRole(searchParams.get('role')),
    };
  }, [searchParams]);

  const apiParams = useMemo<AdminUserListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.role !== 'all' ? { role: state.role } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          if (options?.resetPage) next.set('page', '1');

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.role !== undefined) apply('role', patch.role, ['all']);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('status');
        next.delete('role');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.status !== 'all' ||
      state.role !== 'all' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
